import React, { memo, useEffect } from 'react'
import { Dictionary } from 'lodash'
import groupBy from 'lodash/groupBy'
import { LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import { useActivityApi } from 'apps/fleet-planner/hooks/useActivityApi'
import { obscurePostDeliveryStatus } from 'apps/fleet-planner/pages/loads/utils'
import { IStop } from 'apps/fleet-planner/types'
import { IActivity } from 'apps/fleet-planner/types/activity'
import { ActivityGroupHeader } from './ActivityGroupHeader'
import { ActivityRow } from './ActivityRow'
import 'scss/5_components/_activity-feed.scss'

interface ILoadActivityProps {
  loadId?: string
  loadStops?: IStop[]
  isExternal?: boolean
}

interface IActivityGroupProps {
  date: string
  activities: IActivity[]
  loadStops?: IStop[]
  isExternal?: boolean
}

/**
 * Group activities by day
 * Key: the date of the activity -> ex '2020-11-24'
 * Value: IActivity[] array of activities
 */
const groupActivityByDay = (activityList: IActivity[]): Dictionary<IActivity[]> => {
  const listWithDays = activityList.map(activity => ({
    ...activity,
    day: activity.createdAt.split('T')[0],
  }))
  return groupBy(listWithDays, 'day')
}

const ActivityGroup = memo<IActivityGroupProps>(
  ({ date, activities = [], loadStops = [], isExternal }) => {
    const [firstActivity] = activities || []
    const { createdAt = date } = firstActivity || {} // default value

    return (
      <>
        <ActivityGroupHeader currentDate={createdAt} />
        {activities.map(activity => {
          return (
            <ActivityRow
              activity={activity}
              loadStops={loadStops}
              key={activity.id}
              isExternal={isExternal}
            />
          )
        })}
      </>
    )
  }
)

export const LoadActivity = memo<ILoadActivityProps>(({ loadId, loadStops = [], isExternal }) => {
  const { activityList, getActivities, isLoading } = useActivityApi()

  // Fetch activities
  useEffect(() => {
    if (!isLoading) {
      getActivities(loadId)
    }
  }, [loadId])

  // group the activities by day using Map
  const filterActivties = isExternal
  const filteredActivities = filterActivties
    ? activityList.filter((activity, index) => {
        const { metadata } = activity || {}
        const { status: loadStatus } = metadata || {}
        if (!loadStatus) return true
        const currentStatus = obscurePostDeliveryStatus(loadStatus, true)

        const nextActivity = activityList[index + 1] || null
        const { metadata: nextMetadata } = nextActivity || {}
        const { status: nextLoadStatus } = nextMetadata || {}
        if (!nextLoadStatus) return true
        const nextStatus = obscurePostDeliveryStatus(nextLoadStatus, true)

        const hideActivity = currentStatus === nextStatus && currentStatus === LOAD_STATE.Delivered
        return !hideActivity
      })
    : activityList
  const activitiesByDay = groupActivityByDay(filteredActivities)

  return (
    <div data-test='load-activity' className='pb4'>
      {Object.keys(activitiesByDay).map(day => {
        const activities = activitiesByDay[day]
        const date = filteredActivities[0].day || ''
        return (
          <ActivityGroup
            key={day}
            date={date}
            activities={activities}
            loadStops={loadStops}
            isExternal={isExternal}
          />
        )
      })}
    </div>
  )
})
